import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import { StudentPracticeBookLevel } from '@monorepo/shared/src/types';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import WithQuery from 'components/layout/WithQuery';
import useAssignmentsStore from 'graph/state/query-hooks/use-assignments-store';
import {
  AssignmentActivity as AssignmentActivityType,
  BookType,
} from '@monorepo/shared/src/graph/schema-generated-types-student';
import { ActivityTypes } from 'types';
import useUserStore from 'graph/state/query-hooks/use-user';
import AssignmentActivity from './AssignmentActivity';
import AssignmentLetterBook from './AssignmentLetterBook';
import { AssignmentWithCover } from '../types';
import { fetchAndSetAssignmentsCovers, getAssignmentActivityCompletionStatus } from '../util';
import useAudioInstructions from 'state/hooks/use-audio-instructions';

const Assignment: React.FC = () => {
  const [user] = useUserStore();
  const [
    { assignments, activitySelectSound, isLoading: loadingAssignments, error: assignmentsError },
  ] = useAssignmentsStore();

  const [{ books, loading: loadingLetterBooks, error: letterBooksError }] = usePageStore(
    BookType.LETTER_BOOK
  );
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<AssignmentWithCover | null>(null);
  const [wdtActivity, setWDTActivity] = useState<AssignmentActivityType | null>(null);
  const [practiceBookActivity, setPracticeBookActivity] = useState<AssignmentActivityType | null>(
    null
  );
  const [loadingAssignmentCover, setLoadingAssignmentCover] = useState<boolean>(true);
  const classes = styles();

  const setAssignmentWithCover = async (books, assignments) => {
    const assignmentsWithCovers = await fetchAndSetAssignmentsCovers(books, assignments);

    setAssignment(assignmentsWithCovers[0] as AssignmentWithCover);
    setLoadingAssignmentCover(() => false);
  };

  const onlyPreKGroup =
    user?.me?.groups?.length === 1 && user?.me?.groups[0].name === StudentPracticeBookLevel.PreK;

  useEffect(() => {
    if (
      assignments &&
      assignments.length &&
      books &&
      books.length &&
      !loadingAssignments &&
      !loadingLetterBooks
    ) {
      const filteredAssignments = assignments.filter(
        (assignment) => assignment.id.toString() === assignmentId
      );

      if (!filteredAssignments?.length) navigate('/');

      const filteredBooks = books.filter((book) =>
        book.characters.find((character) => character.id === filteredAssignments[0]?.characterId)
      );

      setAssignmentWithCover(filteredBooks, filteredAssignments);
      setWDTActivity(() =>
        filteredAssignments[0].assignmentActivities?.find(
          ({ activity: { activityType } }) => activityType.name === ActivityTypes.WDT || null
        )
      );
      setPracticeBookActivity(
        () =>
          filteredAssignments[0].assignmentActivities?.find(
            ({ activity: { activityType } }) => activityType.name === ActivityTypes.PRACTICE_BOOK
          ) || null
      );
    }
  }, [assignments, books, loadingAssignments, loadingLetterBooks]);

  useAudioInstructions(
    activitySelectSound,
    !loadingLetterBooks &&
      !loadingAssignments &&
      !loadingAssignmentCover &&
      !assignmentsError &&
      !letterBooksError
  );

  return (
    <WithQuery
      loading={loadingAssignments || loadingAssignmentCover || loadingLetterBooks}
      error={assignmentsError || letterBooksError || (!loadingAssignmentCover && !assignment)}
    >
      <div className={classes.outerContainer}>
        <div className={classes.contentContainer}>
          <div className={classes.activitiesContainer}>
            {assignment?.assignmentActivities
              ?.filter(({ activity: { activityType } }) => activityType?.required)
              .map((assignmentActivity) => (
                <AssignmentActivity
                  key={assignmentActivity.id}
                  assignment={assignment}
                  assignmentActivity={assignmentActivity}
                  assignmentId={+assignmentId!}
                  status={getAssignmentActivityCompletionStatus(assignmentActivity)}
                />
              ))}
          </div>
          <div className={classes.letterBookContainer}>
            <div className={classes.assignmentLetterBook}>
              <AssignmentLetterBook assignment={assignment as AssignmentWithCover} />
            </div>
            <div className={classes.assignmentBtnContainer}>
              <AssignmentActivity
                assignment={assignment as AssignmentWithCover}
                assignmentActivity={wdtActivity as AssignmentActivityType}
                assignmentId={+assignmentId!}
                status={getAssignmentActivityCompletionStatus(
                  wdtActivity as AssignmentActivityType
                )}
              />
              {!onlyPreKGroup && (
                <AssignmentActivity
                  assignment={assignment as AssignmentWithCover}
                  assignmentActivity={practiceBookActivity as AssignmentActivityType}
                  assignmentId={+assignmentId!}
                  status={getAssignmentActivityCompletionStatus(
                    practiceBookActivity as AssignmentActivityType
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </WithQuery>
  );
};

const styles = makeStyles(() => ({
  outerContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  contentContainer: {
    height: remCalc(650),
    width: remCalc(850),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gridGap: remCalc(24),
    '@media (orientation: portrait)': {
      width: remCalc(700),
    },
  },
  activitiesContainer: {
    flex: 1,
    flexWrap: 'wrap',
    '& > *': {
      padding: remCalc(12),
      flexGrow: 0,
      maxWidth: '50%',
      flexBasis: '50%',
      marginTop: remCalc(30),
    },
  },
  letterBookContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: remCalc(24),
  },
  assignmentLetterBook: {
    marginTop: remCalc(72),
    height: remCalc(345),
    '@media (orientation: portrait)': {
      marginTop: remCalc(52),
      height: remCalc(335),
    },
  },
  assignmentBtnContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    width: '100%',
  },
}));

export default Assignment;
